// SOARLIFE FERTILITY ROADMAP™ — content library
// Pathways, timelines, education sections, and checklists.
// Group items may be plain strings or { name, desc } objects (rendered as detail cards).

export const EVALUATION = {
  id: "evaluation",
  title: "Fertility Evaluation",
  intro:
    "Most fertility clinics begin with testing before treatment. Understanding your results helps you and your provider choose the right path.",
  groups: [
    {
      label: "Female testing may include",
      items: [
        "AMH (Anti-Müllerian Hormone)",
        "FSH",
        "LH",
        "Estradiol",
        "Progesterone",
        "TSH",
        "Vitamin D",
        "Infectious Disease Screening",
        "Pelvic Ultrasound",
        "HSG",
        "Saline Sonogram",
        "Genetic Carrier Screening",
      ],
    },
    {
      label: "Male testing may include",
      items: [
        "Semen Analysis",
        "Testosterone",
        "Hormone Testing",
        "Infectious Disease Screening",
        "Genetic Carrier Screening",
      ],
    },
  ],
  checklist: [
    { key: "eval:health-history", label: "Complete health history" },
    { key: "eval:upload-records", label: "Upload records" },
    { key: "eval:upload-medications", label: "Upload medications" },
    { key: "eval:verify-insurance", label: "Verify insurance" },
    { key: "eval:provider-questions", label: "Add provider questions" },
  ],
};

export const AMH_EDUCATION = {
  id: "amh",
  title: "Understanding AMH",
  paragraphs: [
    "AMH stands for Anti-Müllerian Hormone.",
    "AMH helps estimate ovarian reserve, meaning the number of eggs remaining in the ovaries.",
    "AMH does not measure egg quality.",
  ],
  groups: [
    {
      label: "AMH may help providers estimate",
      items: ["Ovarian reserve", "Response to fertility medications", "IVF planning"],
    },
  ],
  notes: [
    { heading: "Higher AMH", text: "May indicate more available follicles." },
    { heading: "Lower AMH", text: "May indicate diminished ovarian reserve." },
    {
      heading: "Remember",
      text: "A low AMH does not automatically mean pregnancy is impossible.",
    },
  ],
  suggestedQuestions: [
    "How does my AMH affect treatment?",
    "Should I consider fertility preservation?",
    "What additional testing is recommended?",
  ],
};

export const PRE_TRANSFER = {
  id: "pre-transfer",
  title: "Preparing For Embryo Transfer",
  paragraphs: ["Many fertility specialists evaluate the uterus before transfer."],
  groups: [
    { label: "Tests may include", items: ["Saline Sonogram", "Hysteroscopy"] },
  ],
  notes: [
    {
      heading: "What is a hysteroscopy",
      text: "A procedure allowing the doctor to view the inside of the uterus.",
    },
    {
      heading: "The goal may be to identify",
      text: "Polyps · Scar tissue · Fibroids affecting the uterine cavity · Areas of inflammation",
    },
  ],
  checklist: [
    { key: "transfer:review-meds", label: "Medication review" },
    { key: "transfer:follow-instructions", label: "Follow transfer instructions" },
    { key: "transfer:confirm-date", label: "Confirm transfer date" },
    { key: "transfer:transportation", label: "Organize transportation" },
    { key: "transfer:prep-checklist", label: "Complete transfer checklist" },
    { key: "transfer:support", label: "Schedule support if desired" },
  ],
};

export const BLOCKED_TUBES = {
  id: "blocked-tubes",
  title: "Understanding Blocked Fallopian Tubes",
  paragraphs: [],
  groups: [
    {
      label: "What causes blocked tubes? Possible causes include",
      items: ["Previous infection", "Endometriosis", "Scar tissue", "Surgery", "Hydrosalpinx"],
    },
  ],
  notes: [
    {
      heading: "Can tubes be unblocked? Sometimes",
      text: "A physician may discuss laparoscopic surgery to attempt repair.",
    },
    {
      heading: "However",
      text: "Success is not guaranteed — results depend on the severity and location of the blockage.",
    },
    {
      heading: "Next steps",
      text: "Some patients may be advised to consider IVF.",
    },
  ],
};

export const DONOR_EGG_EDU = {
  id: "donor-egg-edu",
  title: "Donor Egg Education Center",
  paragraphs: [
    "As women age, egg quality and chromosomal normality generally decline.",
    "Using donor eggs is a personal decision.",
  ],
  groups: [
    {
      label: "Why providers often discuss donor eggs after age 44",
      items: [
        "Embryo development rates may decrease",
        "Miscarriage rates may increase",
        "IVF success rates using one's own eggs often become lower",
        "Donor eggs may offer a higher chance of pregnancy",
      ],
    },
  ],
  notes: [],
  soarlifeProvides: [
    "Education",
    "Emotional support",
    "Appointment preparation",
    "Questions to discuss with your provider",
  ],
};

export const PREGNANCY_TESTING = {
  id: "pregnancy-testing",
  title: "Pregnancy Testing & The Wait",
  paragraphs: [],
  groups: [
    {
      label: "After IUI",
      items: ["Blood pregnancy testing typically occurs approximately 2 weeks later"],
    },
    {
      label: "After IVF transfer",
      items: ["Blood pregnancy testing (Beta hCG) is usually performed based on clinic protocol"],
    },
    {
      label: "During the wait, your portal provides",
      items: [
        "Daily encouragement",
        "Emotional wellness support",
        "AI education",
        "Nurse-led support options",
      ],
    },
  ],
  notes: [],
};

export const PATHWAYS = [
  {
    id: "natural",
    title: "Trying to Conceive Naturally",
    tagline: "Understanding your cycle and fertile window",
    whatIsIt: "Natural cycle monitoring without fertility medications.",
    whatToExpect: [
      {
        label: "Month 1",
        items: ["Baseline testing", "Ovulation tracking"],
      },
      {
        label: "Monthly",
        items: ["Fertility monitoring", "Lifestyle optimization", "Timing education"],
      },
    ],
    doctorQuestions: [
      "Am I ovulating consistently?",
      "Do I need additional testing?",
      "Should my partner have a semen analysis?",
      "How long should we continue trying naturally?",
    ],
    steps: [
      {
        title: "Fertility Evaluation",
        summary: "Baseline testing before treatment",
        details: ["Most fertility clinics begin with testing before treatment."],
        education: ["evaluation", "amh"],
      },
      {
        title: "Ovulation Tracking",
        summary: "Learn your cycle patterns",
        details: [
          "Track ovulation each cycle — predictor kits and cycle signs help pinpoint timing.",
        ],
      },
      {
        title: "Fertile Window",
        summary: "Monitor the days when conception is possible",
        details: [
          "Monitor the fertile window — typically the 5 days before ovulation and ovulation day itself.",
        ],
      },
      {
        title: "Timed Intercourse",
        summary: "Around ovulation",
        details: ["Timed intercourse around ovulation gives the best natural chance."],
      },
      {
        title: "Pregnancy Testing",
        summary: "When and how to test",
        details: ["Most tests are reliable from the day of a missed period."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "natural:track-cycles", label: "Track cycles" },
      { key: "natural:track-ovulation", label: "Track ovulation" },
      { key: "natural:testing", label: "Complete fertility testing" },
      { key: "natural:symptoms", label: "Record symptoms" },
    ],
  },
  {
    id: "ti",
    title: "Timed Intercourse (TI)",
    tagline: "Identifying ovulation and timing the fertile window",
    whatIsIt:
      "Timed intercourse focuses on identifying ovulation and having intercourse during the fertile window.",
    doctorQuestions: [
      "Am I ovulating consistently?",
      "Do I need additional testing?",
      "Should my partner have a semen analysis?",
      "How long should we continue with timed intercourse?",
    ],
    steps: [
      {
        title: "Ovulation Tracking",
        summary: "Step 1 of the TI timeline",
        details: ["Identify ovulation with tracking, kits, or clinic monitoring."],
        education: ["evaluation"],
      },
      {
        title: "Fertile Window",
        summary: "Step 2 of the TI timeline",
        details: ["Your provider may confirm ovulation timing with tracking or labs."],
      },
      {
        title: "Timed Intercourse",
        summary: "Step 3 of the TI timeline",
        details: ["Intercourse is planned around the confirmed fertile window."],
      },
      {
        title: "Pregnancy Testing",
        summary: "Step 4 of the TI timeline",
        details: ["Testing usually happens about two weeks after ovulation."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "ti:track-cycles", label: "Track cycles" },
      { key: "ti:track-ovulation", label: "Track ovulation" },
      { key: "ti:testing", label: "Complete fertility testing" },
      { key: "ti:symptoms", label: "Record symptoms" },
    ],
  },
  {
    id: "medicated-ti",
    title: "Medicated Timed Intercourse",
    tagline: "Cycle support with fertility medication",
    steps: [
      {
        title: "Baseline (Cycle Days 2-5)",
        summary: "Baseline ultrasound + bloodwork",
        details: ["Baseline ultrasound", "Bloodwork"],
        education: ["evaluation", "amh"],
      },
      {
        title: "Medication (Cycle Days 3-7)",
        summary: "Fertility medication phase",
        details: [],
        medicationCards: [
          { name: "Clomid", desc: "Stimulates ovulation." },
          { name: "Letrozole", desc: "Often prescribed for ovulation disorders including PCOS." },
          { name: "Ovidrel Trigger Shot", desc: "Triggers ovulation timing." },
        ],
      },
      {
        title: "Monitoring (Cycle Days 10-14)",
        summary: "Monitoring ultrasound + hormone monitoring",
        details: [
          "Monitoring ultrasound",
          "Hormone monitoring",
          "Automatic medication and ultrasound reminders are emailed before your appointments.",
        ],
      },
      {
        title: "Trigger Shot",
        summary: "Ovidrel administered",
        details: ["Ovidrel is administered to time ovulation precisely."],
      },
      {
        title: "Timed Intercourse",
        summary: "Based on physician recommendations",
        details: ["Timing follows your physician's recommendations after the trigger."],
      },
      {
        title: "Pregnancy Test",
        summary: "About two weeks later",
        details: ["Your clinic will tell you the exact test day."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "mti:baseline", label: "Complete baseline visit" },
      { key: "mti:meds", label: "Take medication as prescribed" },
      { key: "mti:monitoring", label: "Attend monitoring ultrasounds" },
      { key: "mti:trigger", label: "Trigger shot completed" },
    ],
    prepareChecklist: {
      title: "What to Prepare",
      items: [
        { key: "mti:reminders", label: "Medication reminders enabled" },
        { key: "mti:calendar", label: "Calendar updated" },
        { key: "mti:transport", label: "Transportation arranged" },
        { key: "mti:questions", label: "Questions saved" },
      ],
    },
  },
  {
    id: "iui",
    title: "IUI (Intrauterine Insemination)",
    tagline: "The IUI journey, step by step",
    doctorQuestions: [
      "When should I expect ovulation?",
      "How many follicles developed?",
      "What are the chances of success?",
      "What happens if this cycle is unsuccessful?",
    ],
    steps: [
      {
        title: "Before Starting IUI",
        summary: "Important requirement",
        details: [
          "At least one fallopian tube should be open.",
          "Testing may include: HSG, Saline Sonogram.",
        ],
        education: ["evaluation", "blocked-tubes"],
      },
      {
        title: "Baseline Appointment",
        summary: "Ultrasound + bloodwork",
        details: ["Ultrasound", "Bloodwork"],
      },
      {
        title: "Ovulation Stimulation",
        summary: "Possible medications",
        details: [],
        medicationCards: [
          { name: "Letrozole", desc: "Often prescribed for ovulation support." },
          { name: "Clomid", desc: "Stimulates ovulation." },
          { name: "Injectable medications", desc: "Used when a stronger response is needed." },
        ],
      },
      {
        title: "Monitoring Appointments",
        summary: "Ultrasounds + hormone testing",
        details: ["Ultrasounds", "Hormone testing"],
      },
      {
        title: "Trigger Shot",
        summary: "Ovidrel",
        details: ["Ovidrel times ovulation for the procedure."],
      },
      {
        title: "IUI Procedure",
        summary: "Insemination day",
        details: ["Prepared sperm is placed into the uterus."],
      },
      {
        title: "Two Week Wait",
        summary: "Your portal supports you through it",
        details: [
          "Daily encouragement",
          "Symptom journal (wellness check-in notes)",
          "Wellness check-ins",
          "Educational support",
        ],
      },
      {
        title: "Pregnancy Testing",
        summary: "About two weeks after IUI",
        details: ["Blood pregnancy testing typically occurs approximately 2 weeks later."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "iui:tubes", label: "Tube testing completed (HSG / saline sonogram)" },
      { key: "iui:monitoring", label: "Attend monitoring appointments" },
      { key: "iui:procedure", label: "IUI procedure completed" },
      { key: "iui:wellness", label: "Daily wellness check-ins during the wait" },
    ],
  },
  {
    id: "ivf",
    title: "IVF (In Vitro Fertilization)",
    tagline: "The complete IVF journey in seven steps",
    steps: [
      {
        title: "Step 1 · Testing & Preparation",
        summary: "Required testing may include",
        details: [
          "Hormone testing",
          "AMH",
          "Vitamin D",
          "Infectious disease screening",
          "Genetic screening",
          "Semen analysis",
          "Ultrasound evaluation",
        ],
        education: ["evaluation", "amh"],
      },
      {
        title: "Step 2 · Ovarian Stimulation",
        summary: "Daily injectable medications may include",
        details: [],
        medicationCards: [
          { name: "Gonal-F", desc: "Daily injectable stimulation medication." },
          { name: "Follistim", desc: "Daily injectable stimulation medication." },
          { name: "Menopur", desc: "Daily injectable stimulation medication." },
        ],
        extraGroups: [
          {
            label: "Monitoring visits",
            items: ["Bloodwork", "Ultrasounds", "Medication adjustments"],
          },
        ],
      },
      {
        title: "Step 3 · Trigger Injection",
        summary: "Final egg maturation before retrieval",
        details: ["The trigger injection completes final egg maturation before retrieval."],
      },
      {
        title: "Step 4 · Egg Retrieval",
        summary: "Procedure performed under sedation",
        details: ["Egg retrieval is a procedure performed under sedation to collect mature eggs."],
      },
      {
        title: "Step 5 · Fertilization",
        summary: "Eggs combined with sperm in the laboratory",
        details: ["Eggs and sperm are combined in the laboratory."],
      },
      {
        title: "Step 6 · Embryo Development",
        summary: "Watching embryos grow",
        details: [
          "Day 1 — Fertilization Report",
          "Day 3 — Development Update",
          "Day 5-7 — Blastocyst Development",
        ],
      },
      {
        title: "Step 7 · Genetic Testing (Optional)",
        summary: "PGT may be discussed depending on individual circumstances",
        details: [
          "Benefits — screens embryos for chromosomal abnormalities before transfer.",
          "Limitations — not a guarantee of pregnancy; discuss accuracy and results with your provider.",
          "Costs — often billed separately; verify coverage.",
          "Use the question builder below to prepare questions for your provider.",
        ],
        education: ["pre-transfer", "pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "ivf:testing", label: "Complete all baseline testing" },
      { key: "ivf:stimulation", label: "Stimulation monitoring attended" },
      { key: "ivf:retrieval", label: "Egg retrieval completed" },
      { key: "ivf:transfer-prep", label: "Transfer preparation checklist done" },
    ],
  },
  {
    id: "egg-freezing",
    title: "Egg Freezing",
    tagline: "Fertility preservation on your timeline",
    steps: [
      {
        title: "Fertility Evaluation",
        summary: "Reserve and hormone testing",
        details: ["AMH and hormone labs guide your preservation plan."],
        education: ["evaluation", "amh"],
      },
      {
        title: "Ovarian Stimulation",
        summary: "Medication and monitoring",
        details: ["Similar to IVF — bloodwork and ultrasounds track follicle growth."],
      },
      {
        title: "Egg Retrieval",
        summary: "Procedure performed under sedation",
        details: ["Egg retrieval is a procedure performed under sedation to collect mature eggs."],
      },
      {
        title: "Freezing & Storage",
        summary: "Vitrification and storage",
        details: ["Mature eggs are frozen and stored for future use."],
      },
    ],
    checklist: [
      { key: "ef:testing", label: "Complete reserve testing" },
      { key: "ef:retrieval", label: "Egg retrieval completed" },
      { key: "ef:storage", label: "Storage plan confirmed" },
    ],
  },
  {
    id: "donor-egg",
    title: "Donor Egg IVF",
    tagline: "Understanding and navigating donor conception",
    steps: [
      {
        title: "Donor Egg Education Center",
        summary: "Education first",
        details: ["Using donor eggs is a personal decision — learn before you decide."],
        education: ["donor-egg-edu"],
      },
      {
        title: "Donor Selection",
        summary: "Choosing a donor",
        details: ["Your clinic or agency guides matching, screening, and legal steps."],
      },
      {
        title: "Uterine Preparation",
        summary: "Preparing for transfer",
        details: ["Medication prepares the uterine lining for transfer."],
        education: ["pre-transfer"],
      },
      {
        title: "Embryo Transfer",
        summary: "Transfer day",
        details: ["The embryo is transferred to the uterus in a brief procedure."],
      },
      {
        title: "Pregnancy Testing",
        summary: "Result day",
        details: ["Blood testing follows your clinic's protocol after the wait."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "de:education", label: "Review donor egg education" },
      { key: "de:donor", label: "Donor selected" },
      { key: "de:prep", label: "Uterine preparation completed" },
    ],
  },
  {
    id: "exploring",
    title: "Exploring Fertility Options",
    tagline: "Learn first, decide when you're ready",
    steps: [
      {
        title: "Fertility Evaluation",
        summary: "Know your baseline",
        details: ["Testing gives you information — not obligation."],
        education: ["evaluation", "amh"],
      },
      {
        title: "Learn the Pathways",
        summary: "TI, IUI, IVF, preservation, donor options",
        details: ["Explore each pathway at your own pace in this roadmap."],
        education: ["blocked-tubes"],
      },
      {
        title: "Questions For Your Provider",
        summary: "Prepare for a consultation",
        details: ["Use the AI question builder to walk in prepared."],
      },
      {
        title: "Choose With Support",
        summary: "Soarlife walks with you",
        details: ["A nurse-led advocate can join your appointments virtually or in person."],
      },
    ],
    checklist: [
      { key: "ex:evaluation", label: "Complete fertility evaluation" },
      { key: "ex:questions", label: "Build provider questions" },
      { key: "ex:consult", label: "Book a New Client Consultation" },
    ],
  },
];

export const EDUCATION_SECTIONS = {
  evaluation: EVALUATION,
  amh: AMH_EDUCATION,
  "pre-transfer": PRE_TRANSFER,
  "blocked-tubes": BLOCKED_TUBES,
  "donor-egg-edu": DONOR_EGG_EDU,
  "pregnancy-testing": PREGNANCY_TESTING,
};

export const ACTION_CENTER = {
  supportOptions: [
    "Virtual Fertility Advocate",
    "Orlando In-Person Fertility Advocate",
    "Haitian Creole Interpreter",
    "Family Support Session",
  ],
  languages: ["English", "Haitian Creole", "French"],
};

export const UI = {
  portal: "Portal",
  tagline: "Your treatment navigation center",
  scoreTitle: "Journey Readiness Score",
  scoreParts: {
    history: "Medical history completed",
    documents: "Documents uploaded",
    questions: "Questions prepared",
    appointments: "Appointments confirmed",
    medications: "Medications tracked",
    wellness: "Wellness check-ins",
  },
  choosePathway: "Choose your pathway",
  whereAreYou: "Where are you on your journey",
  pickHint: "Select the path that fits you — you can change it anytime.",
  yourPathway: "Your pathway",
  changePathway: "Change pathway",
  pathwayProgress: "Pathway progress",
  whatToExpectSteps: "What to expect — step by step",
  checklistSuffix: "checklist",
  whatIsIt: "What is it?",
  whatToExpect: "What to expect",
  doctorQuestions: "Questions to ask your doctor",
  generateQuestions: "Generate questions for my doctor",
  questionsForProvider: "Questions for your provider",
  couldNotGenerate: "Could not generate questions right now — please try again.",
  suggestedQuestionsLabel: "Suggested questions",
  actionCenter: "Soarlife Action Center",
  acExpect: "What To Expect",
  acQuestions: "Questions For My Doctor",
  acAppointments: "My Appointments",
  acReminders: "Medication Reminders",
  acUpload: "Upload Documents",
  acAttend: "Attend With Me",
  acAI: "Ask Soarlife AI",
  supportOptions: "Support options",
  upcomingAppointments: "Upcoming appointments",
  noAppointments:
    "Your nurse will add appointments here — preparation reminders are emailed automatically.",
  automaticReminders: "Automatic reminders",
  remindersIntro: "For every appointment we email you preparation checklists at:",
  reminderMilestones: [
    ["30 days", "Intake forms · records · insurance"],
    ["14 days", "Health history · medications"],
    ["7 days", "Documents · instructions"],
    ["3 days", "Provider questions"],
    ["24 hrs", "Appointment · medication · transport"],
  ],
  wellnessTitle: "Daily wellness check-in",
  moods: ["Struggling", "Hard day", "Okay", "Hopeful", "Great"],
  checkinPlaceholder: "How are you feeling today? (optional note)",
  saveCheckin: "Save check-in",
  checkinSaved: "Saved — be gentle with yourself",
  myQuestions: "My provider questions",
  noQuestions:
    'Use any "Generate questions for my doctor" button — your question sets collect here.',
  deniedTitle: "The Roadmap is for clients",
  deniedBody:
    "Enter your Soarlife access code in the Client Portal to unlock your Fertility Roadmap.",
  deniedCta: "Go to Client Portal",
};
